import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { EscrowAccountStatus, EscrowFrozenReason } from '@paladin-monorepo/common';
import { clientEscrowApi } from '@/lib/api/client-escrow';
import { Loading } from '@/components/Loading';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';

export function PaymentsEscrowTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const listQuery = useQuery({
    queryKey: ['client-escrow-plans'],
    queryFn: () => clientEscrowApi.listPlans(),
  });

  const freezeMut = useMutation({
    mutationFn: (paymentPlanId: string) => clientEscrowApi.freeze(paymentPlanId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['client-escrow-plans'] });
      void qc.invalidateQueries({ queryKey: ['payment-audit-events'] });
      toast.success(t('payments.escrow.freezeSuccess'));
    },
    onError: () => toast.error(t('payments.escrow.freezeError')),
  });

  const unfreezeMut = useMutation({
    mutationFn: (paymentPlanId: string) => clientEscrowApi.unfreeze(paymentPlanId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['client-escrow-plans'] });
      void qc.invalidateQueries({ queryKey: ['payment-audit-events'] });
      toast.success(t('payments.escrow.unfreezeSuccess'));
    },
    onError: () => toast.error(t('payments.escrow.unfreezeError')),
  });

  if (listQuery.isLoading) {
    return <Loading />;
  }

  const rows = listQuery.data ?? [];

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('payments.escrow.description')}</p>
      {!rows.length ? (
        <p className="text-sm text-muted-foreground">{t('payments.escrow.empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('payments.escrow.columns.project')}</TableHead>
              <TableHead>{t('payments.escrow.columns.currency')}</TableHead>
              <TableHead>{t('payments.escrow.columns.status')}</TableHead>
              <TableHead>{t('payments.escrow.columns.reason')}</TableHead>
              <TableHead className="text-right">{t('payments.escrow.columns.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const frozen = row.escrowStatus === EscrowAccountStatus.FROZEN;
              const manual = row.frozenReason === EscrowFrozenReason.MANUAL;
              return (
                <TableRow key={row.paymentPlanId}>
                  <TableCell className="font-medium">{row.projectTitle}</TableCell>
                  <TableCell>{row.currency}</TableCell>
                  <TableCell>
                    <Badge variant={frozen ? 'destructive' : 'secondary'}>{row.escrowStatus}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{row.frozenReason ?? '-'}</TableCell>
                  <TableCell className="text-right space-x-2">
                    {!frozen ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={freezeMut.isPending}
                        onClick={() => freezeMut.mutate(row.paymentPlanId)}
                      >
                        {t('payments.escrow.freeze')}
                      </Button>
                    ) : manual ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={unfreezeMut.isPending}
                        onClick={() => unfreezeMut.mutate(row.paymentPlanId)}
                      >
                        {t('payments.escrow.unfreeze')}
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">{t('payments.escrow.lockedByCompliance')}</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
